// src/pages/churches/churchList.js
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Table, Button, Tag, Modal } from "antd";
import { getChurches, getChurchById, deleteChurch, provisionChurch } from "./churchService";
import ChurchForm from "./churchForm";
import AddAdminModal from "./components/addAdminModal";
import useNotification from "../../hooks/useNotification";
import Layout from "../../components/layout/layout";
import TableLayout from "../../components/tableLayout/tableLayout";
import TopToolBar from "../../components/topToolBar/topToolBar";
import "./churches.css";

const filtersConfig = {
  status: {
    label: "Status",
    options: [
      { value: "ACTIVE", label: "Active" },
      { value: "PENDING", label: "Pending" },
      { value: "PROVISIONING", label: "Provisioning" },
      { value: "FAILED", label: "Failed" },
      { value: "DISABLED", label: "Disabled" },
      { value: "DELETED", label: "Deleted" }
    ]
  }
};

// Debounce utility
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const PROVISION_ALLOWED_STATUSES = new Set(["PENDING", "FAILED", "ACTIVE", "DISABLED"]);

function ChurchList() {
  const { notification, showNotification, hideNotification } = useNotification();
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});

  // Search
  const [displaySearchTerm, setDisplaySearchTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [provisioningChurchId, setProvisioningChurchId] = useState(null);
  const [polling, setPolling] = useState(false);

  // Add Admin Modal
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [selectedChurchForAdmin, setSelectedChurchForAdmin] = useState(null);

  // Edit
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingChurch, setEditingChurch] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Delete
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingChurch, setDeletingChurch] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /* =========================
     Load Churches
  ========================= */

  const loadChurches = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getChurches();
      setChurches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      showNotification("Failed to load churches", "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadChurches();
  }, [loadChurches]);

  /* =========================
     Search
  ========================= */

  const debouncedSetSearchTerm = useMemo(() => debounce((term) => setSearchTerm(term), 300), []);

  const handleSearch = useCallback(
    (term) => {
      setDisplaySearchTerm(term);
      debouncedSetSearchTerm(term);
    },
    [debouncedSetSearchTerm]
  );

  const globalSearch = useCallback((items, term) => {
    if (!term?.trim()) return items;
    const searchLower = term.toLowerCase().trim();
    return items.filter((item) => {
      const fields = [item.name || "", item.email || "", item.phone || ""];
      return fields.some((field) => field.toLowerCase().includes(searchLower));
    });
  }, []);

  /* =========================
     Filtering
  ========================= */

  const filteredChurches = useMemo(() => {
    let result = [...churches];
    if (filters.status) {
      result = result.filter((c) => c.status === filters.status);
    }
    result = globalSearch(result, searchTerm);
    return result;
  }, [churches, filters, searchTerm, globalSearch]);

  const handleFilterChange = (newFilters) => setFilters(newFilters);

  const handleReset = () => {
    setFilters({});
    setDisplaySearchTerm("");
    setSearchTerm("");
  };

  /* =========================
     Status Rendering
  ========================= */

  const renderStatus = (status) => {
    switch (status) {
      case "ACTIVE":
        return <Tag color="green">ACTIVE</Tag>;
      case "PENDING":
        return <Tag color="orange">PENDING</Tag>;
      case "PROVISIONING":
        return <Tag color="blue">PROVISIONING</Tag>;
      case "FAILED":
        return <Tag color="red">FAILED</Tag>;
      case "DISABLED":
        return <Tag color="default">DISABLED</Tag>;
      case "DELETED":
        return <Tag color="black">DELETED</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  /* =========================
     Provisioning Logic
  ========================= */

  const handleProvision = useCallback(
    async (churchId) => {
      if (polling) return;

      setProvisioningChurchId(churchId);
      setPolling(true);

      setChurches((prev) => prev.map((c) => (c.churchId === churchId ? { ...c, status: "PROVISIONING" } : c)));

      try {
        await provisionChurch(churchId);
        await waitForProvisioning(churchId);
        await loadChurches();
      } catch (err) {
        console.error(err);
        setChurches((prev) => prev.map((c) => (c.churchId === churchId ? { ...c, status: "FAILED" } : c)));
        showNotification("Provisioning failed", "error");
      } finally {
        setProvisioningChurchId(null);
        setPolling(false);
      }
    },
    [polling, loadChurches, showNotification]
  );

  const waitForProvisioning = async (churchId) => {
    const MAX_ATTEMPTS = 40;
    const INTERVAL_MS = 3000;

    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      const church = await getChurchById(churchId);

      setChurches((prev) => prev.map((c) => (c.churchId === churchId ? { ...c, status: church.status } : c)));

      if (church.status === "ACTIVE") return;
      if (church.status === "FAILED") throw new Error("Provisioning failed on server");

      await new Promise((r) => setTimeout(r, INTERVAL_MS));
    }

    throw new Error("Provisioning timed out");
  };

  /* =========================
     Add Admin Modal
  ========================= */

  const openAddAdminModal = useCallback((church) => {
    setSelectedChurchForAdmin(church);
    setShowAddAdminModal(true);
  }, []);

  const closeAddAdminModal = () => {
    setShowAddAdminModal(false);
    setSelectedChurchForAdmin(null);
  };

  /* =========================
     Edit Modal
  ========================= */

  const openEditModal = useCallback((church, e) => {
    e.stopPropagation();
    setEditingChurch(church);
    setShowEditModal(true);
  }, []);

  const confirmEdit = useCallback(() => {
    setShowEditModal(false);
    setShowForm(true);
  }, []);

  /* =========================
     Delete Modal
  ========================= */

  const openDeleteModal = useCallback((church, e) => {
    e.stopPropagation();
    setDeletingChurch(church);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deletingChurch) return;

    setIsDeleting(true);
    try {
      await deleteChurch(deletingChurch.churchId);
      showNotification("Church deleted successfully!", "success");
      setShowDeleteModal(false);
      await loadChurches();
    } catch (err) {
      console.error(err);
      showNotification(err?.response?.data?.message || "Failed to delete church.", "error");
    } finally {
      setIsDeleting(false);
      setDeletingChurch(null);
    }
  }, [deletingChurch, showNotification, loadChurches]);

  /* =========================
     Table Columns
  ========================= */

  const columns = useMemo(
    () => [
      {
        title: "NAME",
        dataIndex: "name",
        render: (name) => name || "-"
      },
      {
        title: "CODE",
        dataIndex: "churchCode",
        render: (code) => code || "-"
      },
      {
        title: "EMAIL",
        dataIndex: "email",
        render: (email) => email || "-"
      },
      {
        title: "PHONE",
        dataIndex: "phone",
        render: (phone) => phone || "-"
      },
      {
        title: "STATUS",
        dataIndex: "status",
        render: (status) => renderStatus(status)
      },
      {
        title: "ACTIONS",
        render: (_, record) => {
          const isProvisioning = record.status === "PROVISIONING";
          const isDeleted = record.status === "DELETED";

          return (
            <div className="church-action-buttons" onClick={(e) => e.stopPropagation()}>
              {record.status === "ACTIVE" && (
                <Button size="small" onClick={() => openAddAdminModal(record)}>
                  Add Admin
                </Button>
              )}

              {PROVISION_ALLOWED_STATUSES.has(record.status) && (
                <Button size="small" loading={provisioningChurchId === record.churchId && isProvisioning} disabled={polling} onClick={() => handleProvision(record.churchId)}>
                  {provisioningChurchId === record.churchId && isProvisioning ? "Provisioning…" : "Provision"}
                </Button>
              )}

              <Button size="small" disabled={isProvisioning || isDeleted} onClick={(e) => openEditModal(record, e)}>
                Edit
              </Button>

              <Button danger size="small" disabled={isProvisioning || isDeleted} onClick={(e) => openDeleteModal(record, e)}>
                Delete
              </Button>
            </div>
          );
        }
      }
    ],
    [provisioningChurchId, polling, openEditModal, openDeleteModal, handleProvision, openAddAdminModal]
  );

  /* =========================
     Render
  ========================= */

  return (
    <Layout
      navTitle="Church Management"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <TableLayout
        toolbar={
          <TopToolBar
            filtersConfig={filtersConfig}
            searchPlaceholder="Search by name, email, or phone..."
            searchTerm={displaySearchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            extraButtons={[
              <Button
                key="add-church"
                className="add-church-btn"
                onClick={() => {
                  setEditingChurch(null);
                  setShowForm(true);
                }}
              >
                Add Church
              </Button>
            ]}
          />
        }
      >
        <Table
          className="church-table"
          rowKey={(record) => record.churchId}
          rowClassName={(_, index) => (index % 2 === 0 ? "table-row-light" : "table-row-dark")}
          columns={columns}
          dataSource={filteredChurches}
          loading={loading}
          pagination={false}
          expandable={{ showExpandColumn: false }}
        />
      </TableLayout>

      {/* Add Admin Modal */}
      {showAddAdminModal && selectedChurchForAdmin && <AddAdminModal church={selectedChurchForAdmin} onClose={closeAddAdminModal} onSuccess={closeAddAdminModal} />}

      {showForm && (
        <ChurchForm
          church={editingChurch}
          onClose={() => {
            setShowForm(false);
            setEditingChurch(null);
          }}
          onSaved={loadChurches}
        />
      )}

      {/* Edit Confirmation Modal */}
      <Modal
        title="Edit Church"
        open={showEditModal}
        onCancel={() => {
          setShowEditModal(false);
          setEditingChurch(null);
        }}
        footer={[
          <Button
            className="chl-modal-cancel-btn"
            key="cancel"
            onClick={() => {
              setShowEditModal(false);
              setEditingChurch(null);
            }}
          >
            Cancel
          </Button>,
          <Button className="chl-modal-confirm-btn" key="confirm" onClick={confirmEdit}>
            Confirm
          </Button>
        ]}
        centered
      >
        <p>
          Are you sure you want to edit <strong>{editingChurch?.name}</strong>?
        </p>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Church"
        open={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeletingChurch(null);
        }}
        footer={[
          <Button
            className="chl-modal-cancel-btn"
            key="cancel"
            onClick={() => {
              setShowDeleteModal(false);
              setDeletingChurch(null);
            }}
          >
            Cancel
          </Button>,
          <Button className="chl-modal-delete-btn" key="confirm" loading={isDeleting} onClick={confirmDelete}>
            Confirm
          </Button>
        ]}
        centered
      >
        <p>
          Are you sure you want to delete <strong>{deletingChurch?.name}</strong>? This action cannot be undone.
        </p>
      </Modal>
    </Layout>
  );
}

export default ChurchList;
